var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '@/components/UI/Loader';
import { blobToFile, canvasToBlob, dpr, loadImageFromSrc } from '../utils/image';
function isMove(s) {
    return s.mode === 'move';
}
function isResize(s) {
    return s.mode === 'resize';
}
const HANDLE = 10; // радиус круглых ручек
export default function CropperModal({ open, onClose, src, onCropped, outputType = 'image/jpeg', quality = 0.9, outName = 'crop.jpg', }) {
    const { t } = useTranslation();
    const canvasRef = useRef(null);
    const overlayRef = useRef(null);
    const [img, setImg] = useState(null);
    const [error, setError] = useState(false);
    // Размер канваса = размер вписанного изображения (без чёрных полей)
    const [viewW, setViewW] = useState(640);
    const [viewH, setViewH] = useState(420);
    // Геометрия изображения в канвасе (совпадает с канвасом)
    const [imgRect, setImgRect] = useState({ x: 0, y: 0, w: 0, h: 0 });
    // Текущий прямоугольник кадрирования
    const [crop, setCrop] = useState({ x: 0, y: 0, w: 0, h: 0 });
    // Режим аспекта: 'free' — свободный, 'card' — 1:2, 'token' — 1:1
    const [aspectMode, setAspectMode] = useState('free');
    const currentAspect = aspectMode === 'free' ? undefined : aspectMode === 'token' ? 1 : 1 / 2;
    // Drag state
    const [drag, setDrag] = useState({ mode: 'none' });
    // Загружаем изображение при открытии
    useEffect(() => {
        if (!open)
            return;
        setError(false);
        loadImageFromSrc(src)
            .then((img) => {
            setImg(img);
        })
            .catch((e) => {
            setImg(null);
            setError(true);
            console.error('Error loading image:', e);
        });
    }, [src, open]);
    // Пересчёт размеров канваса ПОД КАРТИНКУ (без пустых полей)
    useEffect(() => {
        if (!open || !img)
            return;
        const recompute = () => {
            const maxW = Math.min(window.innerWidth - 64, 960);
            const maxH = Math.min(window.innerHeight - 200, 640);
            const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
            const drawW = Math.round(img.naturalWidth * scale);
            const drawH = Math.round(img.naturalHeight * scale);
            setViewW(drawW);
            setViewH(drawH);
        };
        recompute();
        window.addEventListener('resize', recompute);
        return () => window.removeEventListener('resize', recompute);
    }, [open, img]);
    // Рисуем изображение (на весь канвас, без центрирования)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !img)
            return;
        const ratio = dpr();
        canvas.width = Math.round(viewW * ratio);
        canvas.height = Math.round(viewH * ratio);
        canvas.style.width = `${viewW}px`;
        canvas.style.height = `${viewH}px`;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.clearRect(0, 0, viewW, viewH);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality =
            'high';
        ctx.drawImage(img, 0, 0, viewW, viewH);
        // Картинка занимает весь канвас
        setImgRect({ x: 0, y: 0, w: viewW, h: viewH });
        // Стартовый crop: ~80% меньшей стороны, по центру
        const side = Math.round(Math.min(viewW, viewH) * 0.8);
        let w = side, h = side;
        if (currentAspect && currentAspect > 0) {
            if (viewW / viewH > currentAspect) {
                h = Math.round(Math.min(viewH * 0.8, side));
                w = Math.round(h * currentAspect);
            }
            else {
                w = Math.round(Math.min(viewW * 0.8, side));
                h = Math.round(w / currentAspect);
            }
        }
        const cx = Math.round((viewW - w) / 2);
        const cy = Math.round((viewH - h) / 2);
        setCrop({ x: cx, y: cy, w, h });
    }, [img, viewW, viewH, currentAspect]);
    // Оверлей: затемнение + рамка + ручки
    useEffect(() => {
        const overlay = overlayRef.current;
        if (!overlay)
            return;
        const ratio = dpr();
        overlay.width = Math.round(viewW * ratio);
        overlay.height = Math.round(viewH * ratio);
        overlay.style.width = `${viewW}px`;
        overlay.style.height = `${viewH}px`;
        const ctx = overlay.getContext('2d');
        if (!ctx)
            return;
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.clearRect(0, 0, viewW, viewH);
        ctx.fillStyle = 'rgba(0,0,0,.45)';
        ctx.fillRect(0, 0, viewW, viewH);
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        roundRect(ctx, crop.x, crop.y, crop.w, crop.h, 8);
        ctx.fill();
        ctx.restore();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#00e4a7';
        roundRect(ctx, crop.x, crop.y, crop.w, crop.h, 8);
        ctx.stroke();
        drawHandle(ctx, crop.x, crop.y);
        drawHandle(ctx, crop.x + crop.w, crop.y);
        drawHandle(ctx, crop.x, crop.y + crop.h);
        drawHandle(ctx, crop.x + crop.w, crop.y + crop.h);
    }, [crop, viewW, viewH]);
    // --- рисовалки ---
    function roundRect(ctx, x, y, w, h, r) {
        const rr = Math.min(r, w / 2, h / 2);
        ctx.beginPath();
        ctx.moveTo(x + rr, y);
        ctx.arcTo(x + w, y, x + w, y + h, rr);
        ctx.arcTo(x + w, y + h, x, y + h, rr);
        ctx.arcTo(x, y + h, x, y, rr);
        ctx.arcTo(x, y, x + w, y, rr);
        ctx.closePath();
    }
    function drawHandle(ctx, cx, cy) {
        ctx.fillStyle = '#00e4a7';
        ctx.beginPath();
        ctx.arc(cx, cy, HANDLE / 2, 0, Math.PI * 2);
        ctx.fill();
    }
    // --- pointer utils ---
    function whichCorner(px, py) {
        const pts = [
            { corner: 'nw', x: crop.x, y: crop.y },
            { corner: 'ne', x: crop.x + crop.w, y: crop.y },
            { corner: 'sw', x: crop.x, y: crop.y + crop.h },
            { corner: 'se', x: crop.x + crop.w, y: crop.y + crop.h },
        ];
        for (const p of pts) {
            if (Math.hypot(px - p.x, py - p.y) <= HANDLE)
                return p.corner;
        }
        return null;
    }
    function toLocal(e) {
        const rect = e.target.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    function onPointerDown(e) {
        var _a, _b;
        (_b = (_a = e.target).setPointerCapture) === null || _b === void 0 ? void 0 : _b.call(_a, e.pointerId);
        const p = toLocal(e);
        const corner = whichCorner(p.x, p.y);
        if (corner) {
            setDrag({ mode: 'resize', corner, startX: p.x, startY: p.y, origin: Object.assign({}, crop) });
            return;
        }
        if (p.x >= crop.x && p.x <= crop.x + crop.w && p.y >= crop.y && p.y <= crop.y + crop.h) {
            setDrag({ mode: 'move', startX: p.x, startY: p.y, origin: Object.assign({}, crop) });
        }
        else {
            setDrag({ mode: 'none' });
        }
    }
    function onPointerMove(e) {
        const s = drag;
        if (s.mode === 'none')
            return;
        const p = toLocal(e);
        if (isMove(s)) {
            const dx = p.x - s.startX;
            const dy = p.y - s.startY;
            const next = Object.assign(Object.assign({}, s.origin), { x: s.origin.x + dx, y: s.origin.y + dy });
            // держим рамку внутри канваса
            next.x = Math.max(0, Math.min(next.x, viewW - next.w));
            next.y = Math.max(0, Math.min(next.y, viewH - next.h));
            setCrop(next);
            return;
        }
        if (isResize(s)) {
            let { x, y, w, h } = s.origin;
            const dx = p.x - s.startX;
            const dy = p.y - s.startY;
            if (s.corner === 'nw') {
                x += dx;
                y += dy;
                w -= dx;
                h -= dy;
            }
            if (s.corner === 'ne') {
                y += dy;
                w += dx;
                h -= dy;
            }
            if (s.corner === 'sw') {
                x += dx;
                w -= dx;
                h += dy;
            }
            if (s.corner === 'se') {
                w += dx;
                h += dy;
            }
            // мин. размер
            w = Math.max(40, w);
            h = Math.max(40, h);
            // фикс аспекта, если задан
            if (currentAspect && currentAspect > 0) {
                if (s.corner === 'nw' || s.corner === 'se')
                    h = Math.round(w / currentAspect);
                else
                    w = Math.round(h * currentAspect);
            }
            // в границах канваса
            if (x < 0)
                x = 0;
            if (y < 0)
                y = 0;
            if (x + w > viewW)
                w = viewW - x;
            if (y + h > viewH)
                h = viewH - y;
            setCrop({ x, y, w, h });
        }
    }
    function onPointerUp(e) {
        var _a, _b;
        try {
            (_b = (_a = e.target).releasePointerCapture) === null || _b === void 0 ? void 0 : _b.call(_a, e.pointerId);
        }
        catch (_c) {
            // ignore
        }
        setDrag({ mode: 'none' });
    }
    // Обрезка и возврат File
    function doCrop() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!img)
                return;
            const scaleX = img.naturalWidth / imgRect.w; // imgRect.w === viewW
            const scaleY = img.naturalHeight / imgRect.h; // imgRect.h === viewH
            const sx = Math.round((crop.x - imgRect.x) * scaleX); // imgRect.x == 0
            const sy = Math.round((crop.y - imgRect.y) * scaleY); // imgRect.y == 0
            const sw = Math.round(crop.w * scaleX);
            const sh = Math.round(crop.h * scaleY);
            const out = document.createElement('canvas');
            out.width = sw;
            out.height = sh;
            const ctx = out.getContext('2d');
            if (!ctx)
                return;
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality =
                'high';
            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
            const blob = yield canvasToBlob(out, outputType, quality);
            const ext = outputType === 'image/jpeg' ? 'jpg' : outputType.split('/')[1];
            const name = outName.toLowerCase().endsWith(`.${ext}`) ? outName : `${outName}.${ext}`;
            onCropped(blobToFile(blob, name));
            onClose();
        });
    }
    if (!open)
        return null;
    return (_jsx("div", { style: styles.backdrop, "data-cloudinary-modal": true, onClick: onClose, children: _jsxs("div", { style: styles.modal, "data-cloudinary-modal": true, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { style: styles.header, children: [_jsx("div", { children: t('upload.images.cropImage') }), _jsx("button", { onClick: onClose, style: styles.iconBtn, className: "close-btn", "aria-label": "close", children: "x" })] }), _jsx("div", { style: { position: 'relative', width: viewW, height: viewH, margin: '0 auto' }, children: img ? (_jsxs(_Fragment, { children: [_jsx("canvas", { ref: canvasRef, style: { position: 'absolute', inset: 0 } }), _jsx("canvas", { ref: overlayRef, style: {
                                    position: 'absolute',
                                    inset: 0,
                                    cursor: drag.mode === 'none' ? 'default' : 'grabbing',
                                }, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp })] })) : (_jsx(_Fragment, { children: error ? (_jsx("div", { style: {
                                color: 'red',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }, children: t('errors.error') })) : (_jsx(Loader, {})) })) }), _jsxs("div", { style: styles.footer, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsx("span", { style: { color: '#666', fontSize: 11 }, children: t('upload.images.cropInstructions') }), _jsxs("div", { style: styles.radioGroup, children: [_jsx("button", { type: "button", onClick: () => setAspectMode('free'), "aria-pressed": aspectMode === 'free', style: aspectMode === 'free' ? styles.radioActive : styles.radioBtn, title: t('upload.images.cropAspectFree'), children: t('upload.images.cropAspectFree') }), _jsxs("button", { type: "button", onClick: () => setAspectMode('card'), "aria-pressed": aspectMode === 'card', style: aspectMode === 'card' ? styles.radioActive : styles.radioBtn, title: `${t('upload.images.cropAspectCard')} 1:2`, children: [t('upload.images.cropAspectCard'), " 1:2"] }), _jsxs("button", { type: "button", onClick: () => setAspectMode('token'), "aria-pressed": aspectMode === 'token', style: aspectMode === 'token' ? styles.radioActive : styles.radioBtn, title: `${t('upload.images.cropAspectToken')} 1:1`, children: [t('upload.images.cropAspectToken'), " 1:1"] })] })] }), _jsxs("div", { style: { display: 'flex', gap: 8 }, children: [_jsx("button", { onClick: onClose, style: styles.secondaryBtn, children: t('common.cancel') }), _jsx("button", { onClick: doCrop, style: styles.primaryBtn, disabled: !img, children: t('upload.images.crop') })] })] })] }) }));
}
// --- стили ---
const styles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        background: '#fff',
        borderRadius: 12,
        // width: 'min(96vw, 1024px)',
        maxHeight: '90vh',
        padding: '8px 16px',
        boxShadow: '0 20px 50px rgba(0,0,0,.25)',
        overflow: 'auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
        fontWeight: 600,
        fontSize: 16,
    },
    iconBtn: { border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer' },
    footer: { marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    radioGroup: {
        display: 'inline-flex',
        gap: 6,
        marginLeft: 8,
        marginRight: 32,
    },
    radioBtn: {
        padding: '6px 10px',
        borderRadius: 8,
        border: '1px solid #aaa',
        background: '#fff',
        color: '#333',
        cursor: 'pointer',
        fontSize: 12,
    },
    radioActive: {
        padding: '6px 10px',
        borderRadius: 8,
        border: '1px solid #1a73e8',
        background: '#f0f7ff',
        color: '#1a73e8',
        cursor: 'pointer',
        fontSize: 12,
    },
    primaryBtn: {
        padding: '8px 14px',
        borderRadius: 8,
        border: '1px solid #1a73e8',
        background: '#1a73e8',
        color: '#fff',
        cursor: 'pointer',
    },
    secondaryBtn: {
        padding: '8px 14px',
        borderRadius: 8,
        border: '1px solid #aaa',
        background: '#fff',
        color: '#333',
        cursor: 'pointer',
    },
};
